import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { CharacteristicChoice, charAnswer } from "@/lib/scan-ui/characteristic-choice";
import { PartiaAsk } from "@/lib/scan-ui/partia-ask";
import { DUPLICATE_MARK_TEXT, sameMarkIn } from "@/lib/scan-ui/marks";
import {
  ArrowLeft,
  Ban,
  Camera,
  Check,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  TriangleAlert,
  Truck,
  X,
} from "lucide-react";
import React, { Suspense, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  type NeedLine,
  type ResolvedScan,
  type TransferItemCreate,
  type TransferSessionDetail,
  addTransferBoxItems,
  addTransferItem,
  createTransferSession,
  deleteTransferItem,
  finalizeTransferSession,
  isPlanChangedError,
  getTransferScanIndex,
  getTransferSession,
  getTransferSourceStock,
  lookupTransferPartia,
  postTransfer,
  setTransferItemQty,
  reopenTransferSession,
  resolveTransferScan,
  sendTransferSession,
} from "@/api/transfer_check";
import { getOrderCheckConfig } from "@/api/order_check";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { extractErrorMessage, scanErrorTitle } from "@/lib/api-errors";
import { fmtDate, fmtDateTime } from "@/lib/format";
import {
  NEED_STATUS_ORDER,
  REMAINING_EPS,
  boxLineBody,
  buildNeedIndex,
  ck,
  decideBox,
  findNeed,
  granKey,
  notInOrderHero,
  needGranKey,
  needStatus,
  norm,
  plural,
  requiredBoxCoef,
} from "@/lib/order-check";
import { type OrderScan, parseScan } from "@/lib/order-scan";
import { type AcceptedBox, BoxAcceptedCard } from "@/lib/scan-ui/box-accepted";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { type SourceStockState, SourceStockLine } from "./SourceStockLine";
import { FinalizeDialog } from "./FinalizeDialog";
import {
  type GateInfo,
  type PosFilter,
  computeGate,
  filterForGate,
  gateSummary,
} from "./gate";
import { QtyInput } from "@/lib/scan-ui/qty-input";
import { resolveFromIndex, toResolveRequest } from "@/lib/scan-ui/scan-resolve";
import { STATUS_ROW, STATUS_TEXT, fmtQty } from "@/lib/scan-ui/status";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";
import { type BlockedScan, BlockedScanBanner } from "@/pages/fbs/ScanBanner";

import { GOODS_AND_QR_FORMATS } from "@/lib/scan-formats";

interface Draft {
  scan: OrderScan;
  resolved: ResolvedScan;
  charRef: string;
  charName: string;
  withoutChar: boolean;
  partiaRef: string;
  partiaName: string;
  partiaSource: string;
  withoutPartia: boolean;
  qty: number;
  /** `extra` — (ном, хар) нет в заказе: подтверждение «дописать в заказ». */
  mode: "char" | "partia" | "extra" | "normal";
}

type Hero =
  | { kind: "idle" }
  | { kind: "busy"; raw: string }
  | { kind: "ok"; item: HeroItem }
  | { kind: "box"; box: AcceptedBox }
  | {
      kind: "error";
      title: string;
      detail: string;
      raw: string;
      retry?: () => void;
    };

interface HeroItem {
  article: string;
  name: string;
  char: string;
  partia: string;
  coef: number;
  added: number;
  /** Строка заказа под скан; `null` — позиции нет в заказе (допишется). */
  need: NeedLine | null;
  itemId: string;
  /** Базовая единица номенклатуры — для позиции не из заказа. */
  unit: string;
}

// ─────────────────────────── entry ────────────────────────────────────────
export function TransferCheckSessionPageV2(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const navigate = useNavigate();
  const { ref } = useParams<{ ref: string }>();
  const sessionQ = useQuery({
    queryKey: ["tcv2-session", ref],
    queryFn: () => createTransferSession(ref!),
    enabled: !!ref,
  });

  async function startNewCheck() {
    await createTransferSession(ref!, true);
    await sessionQ.refetch();
  }

  return (
    <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
      {sessionQ.isLoading && (
        <div className="flex flex-1 items-center justify-center text-slate-400">
          <Loader2 className="size-7 animate-spin" />
        </div>
      )}
      {sessionQ.isError && (
        <div className="m-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Не удалось открыть проверку:{" "}
          {(sessionQ.error as Error)?.message ?? "ошибка"}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => navigate("/transfer-check")}
          >
            К списку
          </Button>
        </div>
      )}
      {sessionQ.data && (
        <SessionBody
          key={sessionQ.data.id}
          sessionId={sessionQ.data.id}
          onNewCheck={startNewCheck}
        />
      )}
    </div>
  );
}

// ─────────────────────────── S2 body ──────────────────────────────────────

/** Подпись к количеству: коробочная строка — «кор.», иначе БАЗОВАЯ единица
 *  номенклатуры из 1С («шт», «упак»).
 *
 * ⚠️ Слово «шт» здесь было ЗАШИТО в восьми местах. У перчаток базовая единица —
 * «упак» (классификатор ОКЕИ 778, по сто пар в пачке), и экран показывал
 * «осталось 10 шт» там, где заказ требует десять УПАКОВОК: количество верное,
 * врёт слово, а кладовщик несёт десять перчаток. Пусто — единицы у
 * номенклатуры в 1С нет; тогда лучше без слова, чем со словом наугад. */
function unitLabel(n: { unit_coef: number; base_unit_name?: string | null }): string {
  return n.unit_coef > 1 ? "кор." : (n.base_unit_name ?? "");
}

/** Количество со своей подписью; без подписи, если единица неизвестна. */
function withUnit(qty: number, unit: string): string {
  return unit ? `${fmtQty(qty)} ${unit}` : fmtQty(qty);
}

/** Единица по номенклатуре — для позиций НЕ из документа.
 *
 * У «лишнего» своей строки в заказе нет, но номенклатура та же, что у какой-то
 * строки, — чужой там обычно партия или характеристика. Тогда базовая единица
 * известна. Не нашлась — показываем число без слова. */
function unitByNom(
  needs: { nomenclature_ref: string; unit_coef: number; base_unit_name?: string | null }[],
  ref: string,
): string {
  const n = needs.find((x) => x.nomenclature_ref === ref);
  return n ? (n.base_unit_name ?? "") : "";
}

/** Единица ИТОГА по документу — только если она у всех строк одна.
 *
 * Сумма по заказу складывает разнородное: сорок канистр (шт) и двадцать
 * упаковок перчаток. Подписать такую сумму любым словом значит соврать, а
 * молчание тут честно: число мест видно, единица — нет. */
function commonUnit(needs: { unit_coef: number; base_unit_name?: string | null }[]): string {
  const names = new Set(needs.map(unitLabel));
  return names.size === 1 ? ([...names][0] ?? "") : "";
}

/** Цвета статусов строки заказа в этом разделе: перебор — не ошибка, а
 *  подсветка (заказ подгонится под факт), поэтому янтарь, а не красный общего
 *  `STATUS_ROW`/`STATUS_TEXT` (тот остаётся у сборки, где перебор — брак). */
const ROW_CLS: typeof STATUS_ROW = { ...STATUS_ROW, over: "border-amber-400 bg-amber-100" };
const TEXT_CLS: typeof STATUS_TEXT = { ...STATUS_TEXT, over: "text-amber-800" };

function SessionBody({
  sessionId,
  onNewCheck,
}: {
  sessionId: string;
  onNewCheck: () => Promise<void>;
}): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detailKey = ["tcv2-detail", sessionId];
  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => getTransferSession(sessionId),
  });
  // Остаток склада-источника — отдельный запрос (detail идёт на каждый скан и
  // не должен ждать регистр), но обновляется тем же refetch, что и detail.
  const sourceStockKey = ["tcv2-source-stock", sessionId];
  const sourceStockQ = useQuery({
    queryKey: sourceStockKey,
    queryFn: () => getTransferSourceStock(sessionId),
  });
  const refetch = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: detailKey }),
      qc.invalidateQueries({ queryKey: sourceStockKey }),
    ]);

  const configQ = useQuery({
    queryKey: ["oc-config"],
    queryFn: getOrderCheckConfig,
    staleTime: 300000,
  });
  const scanMethod = configQ.data?.scan_method ?? "tsd";

  const scanIndexQ = useQuery({
    queryKey: ["tcv2-scan-index", sessionId],
    queryFn: () => getTransferScanIndex(sessionId),
    staleTime: 3600000,
    gcTime: 3600000,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  const detail = detailQ.data;
  const [hero, setHero] = useState<Hero>({ kind: "idle" });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sheet, setSheet] = useState<
    null | { kind: "positions"; filter: PosFilter } | { kind: "items" }
  >(null);
  const [dialog, setDialog] = useState<null | "fin" | "send" | "reopen" | "clear">(null);
  const [cameraScan, setCameraScan] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [blocked, setBlocked] = useState<BlockedScan | null>(null);

  const busy = hero.kind === "busy";
  const scanEnabled =
    detail?.header.status === "open" &&
    !draft &&
    !sheet &&
    !dialog &&
    !cameraScan &&
    !busy;
  const resolvingRef = useRef(false);

  // ── обработка скана ────────────────────────────────────────────────────
  async function handleScan(raw: string) {
    if (detail?.header.status !== "open" || draft || sheet || dialog) return;
    if (resolvingRef.current) {
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    resolvingRef.current = true;
    setBlocked(null);
    try {
      await processScan(raw);
    } finally {
      resolvingRef.current = false;
    }
  }

  async function processScan(raw: string) {
    const scan = parseScan(raw);
    const req = toResolveRequest(scan);
    if (!req) {
      setHero({
        kind: "error",
        title: "Не распознан код",
        detail: "Это не ШК/GTIN/GS1/DataMatrix товара",
        raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    // Та же штука ЧЗ второй раз в сессию — отказ сразу, без 1С (бэк — 409).
    if (sameMarkIn(scan, detail?.items ?? [])) {
      setHero({ kind: "error", title: "Уже отсканировано", detail: DUPLICATE_MARK_TEXT, raw });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    let resolved = resolveFromIndex(scan, scanIndexQ.data) ?? undefined;
    if (!resolved) {
      setHero({ kind: "busy", raw });
      try {
        resolved = await resolveTransferScan(sessionId, req);
      } catch (e) {
        setHero({
          kind: "error",
          // Состав коробки читается из БД портала, 1С тут ни при чём.
          title: scan.kind === "sscc" ? "Коробку не принять" : scanErrorTitle(e, "Ошибка 1С"),
          detail:
            scan.kind === "sscc"
              ? `Не удалось получить состав коробки: ${extractErrorMessage(e, "сервер не ответил")}`
              : extractErrorMessage(e, "Не удалось связаться с 1С"),
          raw,
          retry: () => handleScan(raw),
        });
        navigator.vibrate?.([60, 60, 60]);
        return;
      }
    }
    if (!resolved.matched) {
      setHero({
        kind: "error",
        // Коробку 1С даже не спрашивали — «товар не найден в 1С» про неё неправда.
        title: resolved.scan_kind === "sscc" ? "Коробку не принять" : "Товар не найден в 1С",
        detail: resolved.message || "Нет в 1С",
        raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    // Вскрытый короб упаковки: принимается ОСТАТОК — сказать об этом сразу.
    if (resolved.notice) toast.warning(resolved.notice, { duration: 10000 });
    if (resolved.box_lines?.length) {
      await acceptBox(scan, resolved);
      return;
    }
    decideAfterResolve(scan, resolved);
  }

  /** Сборная коробка — ЦЕЛИКОМ или никак (решение 22.10.2026): каждая строка
   *  проходит правила одиночного скана этого раздела (`decideBox`: перебор
   *  принимается, не из заказа — только уже подтверждённая), иначе отказ
   *  построчно и ничего не отправлено; прошли все — одна запись `…/items/box`
   *  (гейт свободного остатка — на бэке, по сумме строк). */
  async function acceptBox(scan: OrderScan, resolved: ResolvedScan) {
    const needs = detail?.needs ?? [];
    // Сборная коробка без номера — ответ сломан: без SSCC ни повтор не узнать,
    // ни коробку не назвать. Молча подставлять пустой нельзя.
    const sscc = resolved.sscc;
    if (!sscc) {
      setHero({
        kind: "error",
        title: "Коробку не принять",
        detail: "В ответе сервера нет номера коробки (SSCC) — коробка не принята. Сообщите администратору.",
        raw: scan.raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    const lines = resolved.box_lines ?? [];
    const verdict = decideBox(needs, lines, { doc: "transfer", extras: detail?.extras ?? [] }, {
      sscc,
      items: detail?.items ?? [],
    });
    if (!verdict.ok) {
      setHero({ kind: "error", title: "Коробку не принять", detail: verdict.text, raw: scan.raw });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    try {
      await addTransferBoxItems(sessionId, {
        raw: scan.raw,
        items: lines.map((l) => boxLineBody(l, scan, resolved.scan_kind)),
      });
    } catch (e) {
      // Как у одиночного скана: отказ записи (остаток, повтор коробки, состав)
      // — героем, а не тостом: текст нужен, чтобы пойти разбираться.
      setHero({
        kind: "error",
        title: "Коробку не принять",
        detail: extractErrorMessage(e, "Не удалось записать коробку"),
        raw: scan.raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    navigator.vibrate?.(40);
    setHero({
      kind: "box",
      box: {
        sscc,
        lines: lines.map((l) => ({
          article: l.nomenclature_article,
          name: l.nomenclature_name,
          characteristic: l.characteristic_name,
          partia: l.partia_name,
          qty: l.qty_base,
          unit: unitByNom(needs, l.nomenclature_ref),
        })),
      },
    });
    // ⚠️ Ждём ВНУТРИ замка скана (`resolvingRef`): следующая коробка, пойманная
    // до обновления, решалась бы по старым позициям — и повтор не узнавался.
    await refetch();
  }

  function decideAfterResolve(scan: OrderScan, resolved: ResolvedScan) {
    const needs = detail?.needs ?? [];
    const nom = resolved.nomenclature_ref;
    const base = baseDraft(resolved, scan, "normal");
    // Характеристику/партию уточняем и для товара не из заказа: строка,
    // которую портал допишет в заказ, — по (номенклатура, характеристика).
    if (resolved.needs_characteristic && !base.charRef) {
      setDraft({ ...base, mode: "char" });
      return;
    }
    if (resolved.needs_partia && !base.partiaRef) {
      setDraft({ ...base, mode: "partia" });
      return;
    }
    const scanPartia = base.withoutPartia ? "" : base.partiaRef;
    const scanCoef = resolved.unit_coefficient || 1;
    const need = findNeed(needs, nom, base.charRef, scanPartia, scanCoef);
    const boxCoef = requiredBoxCoef(needs, nom, base.charRef);
    if (!need && boxCoef > 1 && ck(scanCoef) !== ck(boxCoef)) {
      setHero({
        kind: "error",
        title: `Нужны короба ×${fmtQty(boxCoef)}`,
        detail:
          scanCoef > 1
            ? `Отсканирован короб ×${fmtQty(scanCoef)} — не тот размер упаковки.`
            : "Отсканирован штучный код. Эта позиция — в коробах.",
        raw: scan.raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    // (Ном, хар) нет в заказе — «Завершить» допишет строку в заказ. Мисскан
    // чужого товара вероятнее перебора, поэтому первый скан такой позиции —
    // через подтверждение; уже подтверждённую (она в «нет в заказе») дальше
    // принимаем сразу, как и любую строку заказа.
    if (!need) {
      const confirmed = (detail?.extras ?? []).some(
        (e) =>
          e.nomenclature_ref === nom &&
          norm(e.characteristic_ref) === norm(base.charRef),
      );
      if (confirmed) void addResolved(base);
      else setDraft({ ...base, mode: "extra" });
      return;
    }
    // Сверх строки заказа — принимаем сразу: перемещают кратно паллете, а
    // заказ пишет тот, кто её размера не знает. Заказ подгонится на «Завершить».
    void addResolved(base);
  }

  // Подтверждение из шторки — один запрос за раз: двойной тап по «Добавить»
  // иначе слал второй POST и задваивал позицию. Сканы мимо шторки идут через
  // `addResolved` напрямую и этим замком не режутся.
  const [confirming, setConfirming] = useState(false);
  const confirmingRef = useRef(false);
  async function confirmDraft(d: Draft, opts?: { loose?: boolean }) {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    try {
      await addResolved(d, opts);
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
    }
  }

  async function addResolved(d: Draft, opts?: { loose?: boolean }) {
    const r = d.resolved;
    const loose = opts?.loose ?? false;
    const body: TransferItemCreate = {
      scan_kind: r.scan_kind,
      raw: d.scan.raw,
      ean13: d.scan.ean13 ?? null,
      gtin: d.scan.gtin ?? null,
      serial: d.scan.serial ?? null,
      nomenclature_ref: r.nomenclature_ref,
      nomenclature_name: r.nomenclature_name,
      nomenclature_article: r.nomenclature_article,
      characteristic_ref: d.withoutChar ? "" : d.charRef,
      characteristic_name: d.withoutChar ? "" : d.charName,
      partia_ref: d.withoutPartia ? "" : d.partiaRef,
      partia_name: d.withoutPartia ? "" : d.partiaName,
      partia_source:
        !d.withoutPartia && d.partiaRef ? d.partiaSource || "manual" : "none",
      unit_ref: r.unit_ref,
      unit_coefficient: loose ? 1 : r.unit_coefficient,
      qty_scanned: loose ? d.qty : r.qty_scanned,
      qty_base: d.qty,
    };
    try {
      const created = await addTransferItem(sessionId, body);
      navigator.vibrate?.(40);
      const need = findNeed(
        detail?.needs ?? [],
        r.nomenclature_ref,
        d.withoutChar ? "" : d.charRef,
        d.withoutPartia ? "" : d.partiaRef,
        loose ? 1 : r.unit_coefficient,
      );
      setHero({
        kind: "ok",
        item: {
          article: r.nomenclature_article,
          name: r.nomenclature_name,
          char: d.withoutChar ? "" : d.charName,
          partia: d.withoutPartia ? "" : d.partiaName,
          coef: loose ? 1 : r.unit_coefficient,
          added: d.qty,
          need,
          itemId: created.id,
          unit: unitByNom(detail?.needs ?? [], r.nomenclature_ref),
        },
      });
      setDraft(null);
      refetch();
    } catch (e) {
      // Отказ на добавлении — теперь ШТАТНЫЙ исход: гейт свободного остатка
      // отвечает 409 «свободно 0, всего лежит N, снимите резерв». Тоста мало
      // по двум причинам: он исчезает (а текст нужен, чтобы пойти в 1С), и,
      // главное, герой оставался в `busy` — аппбар писал «Ищем в 1С…», перехват
      // скана не включался обратно, и сканер умирал до тапа по экрану.
      setDraft(null);
      setHero({
        kind: "error",
        title: "Позиция не принята",
        detail: extractErrorMessage(e, "Не удалось добавить позицию"),
        raw: d.scan.raw,
      });
      navigator.vibrate?.([60, 60, 60]);
    }
  }

  // Со склада берут пачками, а скан даёт одну упаковку: без степпера «привезли
  // 10 коробов» стоит десяти сканов. Гейт свободного остатка проверяет ДОБАВКУ
  // на бэкенде — обойти его вводом числа нельзя.
  const qtyM = useMutation({
    mutationFn: ({ id, units, coef }: { id: string; units: number; coef: number }) =>
      setTransferItemQty(sessionId, id, units, coef),
    onSuccess: (item) => {
      setHero((h) =>
        h.kind === "ok" ? { ...h, item: { ...h.item, added: item.qty_base } } : h,
      );
      refetch();
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось изменить количество")),
  });
  const undoM = useMutation({
    mutationFn: (itemId: string) => deleteTransferItem(sessionId, itemId),
    onSuccess: () => {
      toast.success("Отменено");
      setHero({ kind: "idle" });
      refetch();
    },
    onError: (e) => toast.error(`Отмена: ${extractErrorMessage(e, "ошибка")}`),
  });
  const removeItemM = useMutation({
    mutationFn: (itemId: string) => deleteTransferItem(sessionId, itemId),
    onSuccess: () => {
      toast.success("Позиция удалена");
      refetch();
    },
    onError: (e) =>
      toast.error(`Удаление: ${extractErrorMessage(e, "ошибка")}`),
  });

  // 409 «собранное изменилось с момента просмотра»: диалог остаётся открытым,
  // предпросмотр перечитывается, причина видна в нём (не тостом).
  const [finalizeStale, setFinalizeStale] = useState<string | null>(null);
  const finalizeM = useMutation({
    mutationFn: (planToken: string) => finalizeTransferSession(sessionId, planToken),
    onMutate: () => setFinalizeStale(null),
    onSuccess: (r) => {
      toast.success(
        r.order_changed
          ? `Перемещение создано · Заказ ${detail?.header.transfer_number ?? ""} изменён под факт`
          : "Перемещение создано",
      );
      setDialog(null);
      refetch();
    },
    onError: (e) => {
      // Состояние сессии/заказа могло сдвинуться (снимок, правка заказа) —
      // перечитываем detail в любом случае.
      refetch();
      if (isPlanChangedError(e)) {
        setFinalizeStale(extractErrorMessage(e, "Собранное изменилось"));
        void qc.invalidateQueries({ queryKey: ["tcv2-order-changes", sessionId] });
        return;
      }
      // Отказ завершения (правка заказа, проведение, остаток) — плашкой, не
      // тостом: текст 1С нужен, чтобы пойти и разобраться, а тост исчезает.
      setDialog(null);
      setHero({
        kind: "error",
        title: "Не завершено",
        detail: extractErrorMessage(e, "Не удалось завершить"),
        raw: "",
      });
    },
  });
  // Документ создаётся НЕ проведённым: «да, товар действительно уехал» —
  // решение человека, а не расчёта. Отказ 1С показываем как есть: обычно это
  // нехватка свободного остатка, и текст — единственная подсказка.
  const postM = useMutation({
    mutationFn: () => postTransfer(sessionId),
    onSuccess: () => {
      toast.success("Перемещение проведено");
      refetch();
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось провести")),
  });
  // «Отправить» (перемещение через приёмку): позиции замораживаются, в 1С
  // ничего. Отказ (гейт остатка, уже отправлено, пусто) — плашкой героя:
  // текст нужен, чтобы пойти разбираться, тост исчезает.
  const sendM = useMutation({
    mutationFn: () => sendTransferSession(sessionId),
    onSuccess: () => {
      toast.success("Отправлено на приёмку");
      setDialog(null);
      setHero({ kind: "idle" });
      refetch();
    },
    onError: (e) => {
      setDialog(null);
      refetch();
      setHero({
        kind: "error",
        title: "Не отправлено",
        detail: extractErrorMessage(e, "Не удалось отправить на приёмку"),
        raw: "",
      });
    },
  });
  const reopenM = useMutation({
    mutationFn: (clear: boolean) => reopenTransferSession(sessionId, clear),
    onSuccess: () => {
      toast.success("Возвращено в работу");
      setDialog(null);
      setHero({ kind: "idle" });
      refetch();
    },
    onError: (e) => {
      setDialog(null);
      toast.error(extractErrorMessage(e, "Не удалось вернуть в работу"));
    },
  });

  // Код пришёл, пока перехват выключен: не теряем молча — называем, почему
  // он не принят. Только открытая проверка: остальные экраны — чтение.
  function onBlocked(raw: string) {
    if (detail?.header.status !== "open") return;
    const reason = busy
      ? "Предыдущий скан ещё обрабатывается — отсканируйте ещё раз."
      : draft
        ? "Сначала ответьте на вопрос в открытом окне, потом сканируйте."
        : cameraScan
          ? "Открыта камера — закройте её, чтобы сканировать сканером."
          : "Сначала закройте открытое окно.";
    setBlocked({ raw, reason });
  }
  const scan = useScanCapture({ enabled: !!scanEnabled, onScan: handleScan, onBlocked });

  if (!detail) {
    return (
      <div className="flex flex-1 items-center justify-center text-slate-400">
        <Loader2 className="size-7 animate-spin" />
      </div>
    );
  }

  if (detail.header.status === "completed") {
    return (
      <CompletedScreen
        detail={detail}
        onNewCheck={onNewCheck}
        onReopen={() => setDialog("reopen")}
        onClear={() => setDialog("clear")}
        onOpenPositions={() => setSheet({ kind: "positions", filter: "all" })}
        onBack={() => navigate("/transfer-check")}
        onPost={() => postM.mutate()}
        postBusy={postM.isPending}
        dialog={dialog}
        onCloseDialog={() => setDialog(null)}
        onConfirmReopen={() => reopenM.mutate(false)}
        onConfirmClear={() => reopenM.mutate(true)}
        reopenBusy={reopenM.isPending}
        sheet={sheet?.kind === "positions" ? sheet : null}
        onCloseSheet={() => setSheet(null)}
        sourceStock={sourceStockQ}
      />
    );
  }

  // Отправлено / расхождение / отменено — только чтение: правки бэк отбивает
  // 409, а «Сканер готов» на таком экране врал бы.
  if (
    detail.header.status === "sent" ||
    detail.header.status === "discrepancy" ||
    detail.header.status === "cancelled"
  ) {
    return (
      <FrozenScreen
        detail={detail}
        onBack={() => navigate("/transfer-check")}
        onNewCheck={onNewCheck}
        sheet={sheet?.kind === "positions" ? sheet : null}
        onOpenPositions={() => setSheet({ kind: "positions", filter: "all" })}
        onCloseSheet={() => setSheet(null)}
        sourceStock={sourceStockQ}
      />
    );
  }

  const gate = computeGate(detail);
  const viaReceipt = detail.header.requires_receipt;

  return (
    <>
      {/* невидимый sticky-input глобального перехвата скана (ТСД шлёт как клавиатура) */}
      <input
        ref={scan.scanInputRef}
        {...scan.scanInputProps}
        className="pointer-events-none fixed left-0 top-0 size-px opacity-0"
      />

      {/* Z1 аппбар */}
      <AppBar
        detail={detail}
        chip={
          busy
            ? "busy"
            : scanIndexQ.isError
              ? "warn"
              : scanEnabled
                ? "ready"
                : "paused"
        }
        onBack={() => navigate("/transfer-check")}
        menu={{
          showCamera: scanMethod === "camera" || scanMethod === "both",
          onCamera: () => setCameraScan(true),
          onManual: () => setManualOpen(true),
          onReloadIndex: () => {
            void scanIndexQ.refetch();
            toast.success("Перезагружаю индекс ШК…");
          },
          onClear: () => setDialog("clear"),
        }}
      />

      {/* Z2 прогресс */}
      <Progress
        detail={detail}
        gate={gate}
        onTap={() =>
          setSheet({ kind: "positions", filter: filterForGate(gate.state) })
        }
      />

      {/* Z4 герой */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {blocked && (
          <div className="mb-2">
            <BlockedScanBanner blocked={blocked} onClose={() => setBlocked(null)} />
          </div>
        )}
        <Hero
          hero={hero}
          remaining={gate.under}
          unit={commonUnit(detail.needs)}
          onUndo={(id) => undoM.mutate(id)}
          onSetQty={(id, units, coef) => qtyM.mutate({ id, units, coef })}
          onDismiss={() => setHero({ kind: "idle" })}
        />
      </div>

      {/* Z5 сводка позиций */}
      <button
        type="button"
        onClick={() => setSheet({ kind: "items" })}
        className="flex items-center justify-between border-t bg-white px-3 py-2 text-left"
      >
        <span className="text-[13px] font-bold">
          Собрано позиций: {detail.items.length}
        </span>
        <span className="flex items-center gap-2 text-[11px] text-slate-500">
          {fmtQty(detail.qty_scanned_total)} шт
          <ChevronRight className="size-4 text-slate-300" />
        </span>
      </button>

      {/* Z6 гейт: через приёмку — «Отправить» вместо «Завершить» */}
      {viaReceipt ? (
        <SendGate
          gate={gate}
          sending={sendM.isPending}
          onTap={() => {
            if (gate.state !== "empty") setDialog("send");
          }}
        />
      ) : (
        <Gate
          gate={gate}
          finalizing={finalizeM.isPending}
          onTap={() => {
            if (gate.state !== "empty") setDialog("fin");
          }}
        />
      )}

      {(scanMethod === "camera" || scanMethod === "both") && !cameraScan && (
        <button
          type="button"
          onClick={() => setCameraScan(true)}
          className="fixed bottom-24 right-4 z-30 flex h-14 items-center gap-2 rounded-full bg-sky-700 px-5 text-[14px] font-bold text-white shadow-lg"
        >
          <Camera className="size-5" /> Сканировать камерой
        </button>
      )}

      {cameraScan && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              // Здесь рядом лежат ДВЕ разные печати: QR-наклейки, которые
              // клеят на приёмке, и заводские EAN13/DataMatrix на упаковке.
              // Дефолтный набор товарный, QR в него не входит — без этого
              // наклейки приёмки камера не видела бы вовсе.
              formats={GOODS_AND_QR_FORMATS}
              onDetect={(raw) => {
                setCameraScan(false);
                handleScan(raw);
              }}
              onClose={() => setCameraScan(false)}
            />
          </div>
        </Suspense>
      )}

      {/* SH1 уточнение */}
      {draft && (
        <ClarifySheet
          sessionId={sessionId}
          draft={draft}
          setDraft={setDraft}
          needs={detail.needs}
          busy={confirming}
          onCancel={() => {
            setDraft(null);
            setHero({ kind: "idle" });
          }}
          onConfirm={(loose, qty) =>
            void confirmDraft({ ...draft, qty: qty ?? draft.qty }, { loose })
          }
        />
      )}

      {/* SH2 позиции */}
      {sheet?.kind === "positions" && (
        <PositionsSheet
          detail={detail}
          sourceStock={sourceStockQ}
          filter={sheet.filter}
          onClose={() => setSheet(null)}
          onDeleteItem={(id) => removeItemM.mutate(id)}
        />
      )}

      {/* Лента позиций */}
      {sheet?.kind === "items" && (
        <ItemsSheet
          detail={detail}
          onClose={() => setSheet(null)}
          onDeleteItem={(id) => removeItemM.mutate(id)}
        />
      )}

      {/* SH4 ручной ввод */}
      {manualOpen && (
        <ManualCodeSheet
          onSubmit={(code) => {
            setManualOpen(false);
            handleScan(code);
          }}
          onClose={() => setManualOpen(false)}
        />
      )}

      {dialog === "fin" && (
        <FinalizeDialog
          sessionId={sessionId}
          busy={finalizeM.isPending}
          staleError={finalizeStale}
          onConfirm={(planToken) => finalizeM.mutate(planToken)}
          onCancel={() => {
            setFinalizeStale(null);
            setDialog(null);
          }}
        />
      )}
      {dialog === "send" && (
        <Dialog
          title="Отправить на приёмку?"
          body={
            <div className="space-y-1">
              <div>
                Куда: <b>{detail.header.to_name || "—"}</b>
              </div>
              <div>
                Позиций: <b>{positionsCount(detail.items)}</b> · всего{" "}
                <b>{withUnit(detail.qty_scanned_total, commonUnit(detail.needs))}</b>
              </div>
              {gate.state === "diff" && (
                <div className="text-amber-800">
                  Отличается от заказа: {gateSummary(gate)}
                </div>
              )}
              <div className="font-semibold text-slate-700">
                В 1С ничего не запишется — документ проведётся после приёмки
                получателем. Менять позиции после отправки нельзя.
              </div>
            </div>
          }
          confirmLabel="Отправить"
          confirmBusy={sendM.isPending}
          onConfirm={() => sendM.mutate()}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === "clear" && (
        <Dialog
          title="Очистить перемещение полностью?"
          body="Все сканы будут удалены."
          confirmLabel="Очистить"
          confirmBusy={reopenM.isPending}
          danger
          onConfirm={() => reopenM.mutate(true)}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}

// ─────────────────────────── helpers ──────────────────────────────────────
function baseDraft(
  resolved: ResolvedScan,
  scan: OrderScan,
  mode: Draft["mode"],
): Draft {
  return {
    scan,
    resolved,
    charRef: resolved.characteristic_ref,
    charName: resolved.characteristic_name,
    withoutChar: false,
    partiaRef: resolved.partia_ref,
    partiaName: resolved.partia_name,
    partiaSource: resolved.partia_source,
    withoutPartia: false,
    qty: resolved.qty_base || 1,
    mode,
  };
}
/** Сколько разных позиций (номенклатура, характеристика, партия) собрано:
 *  строк сканов больше — одну позицию сканируют много раз. */
function positionsCount(
  items: { nomenclature_ref: string | null; characteristic_ref: string | null; partia_ref: string | null }[],
): number {
  return new Set(
    items.map((i) => `${i.nomenclature_ref ?? ""}|${norm(i.characteristic_ref)}|${norm(i.partia_ref)}`),
  ).size;
}
// ─────────────────────────── zones ────────────────────────────────────────
function AppBar({
  detail,
  chip,
  onBack,
  menu,
}: {
  detail: TransferSessionDetail;
  chip: "ready" | "busy" | "paused" | "warn";
  onBack: () => void;
  menu?: {
    showCamera: boolean;
    onCamera: () => void;
    onManual: () => void;
    onReloadIndex: () => void;
    onClear: () => void;
  };
}): React.ReactElement {
  const [info, setInfo] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const h = detail.header;
  const chipMap = {
    ready: {
      dot: "bg-emerald-400",
      bg: "bg-emerald-500/20",
      t: "Сканер готов",
    },
    busy: { dot: "bg-sky-400", bg: "bg-sky-500/20", t: "Ищем в 1С…" },
    paused: { dot: "bg-slate-400", bg: "bg-white/10", t: "Скан на паузе" },
    warn: {
      dot: "bg-amber-400",
      bg: "bg-amber-500/20",
      t: "Индекс ШК не загружен",
    },
  }[chip];
  const item =
    "block w-full px-3 py-2.5 text-left text-[13px] hover:bg-slate-100";
  return (
    <div className="relative bg-slate-900 text-white">
      <div className="flex h-[46px] items-center gap-1 px-1">
        <button
          type="button"
          onClick={onBack}
          className="flex size-9 items-center justify-center"
          aria-label="Назад"
        >
          <ArrowLeft className="size-5" />
        </button>
        <button
          type="button"
          onClick={() => setInfo((s) => !s)}
          className="min-w-0 flex-1 text-left leading-tight"
        >
          <div className="truncate font-mono text-[13px] font-bold">
            {h.transfer_number}
          </div>
          <div className="truncate text-[10px] text-slate-400">
            {h.from_name} → {h.to_name}
          </div>
        </button>
        <button
          type="button"
          onClick={() => menu && setMenuOpen((s) => !s)}
          className={cn(
            "mr-1 flex h-[30px] items-center gap-1.5 rounded-full px-2.5",
            chipMap.bg,
          )}
        >
          <span className={cn("size-2 rounded-full", chipMap.dot)} />
          <span className="text-[11px] font-semibold">{chipMap.t}</span>
        </button>
      </div>
      {info && (
        <div className="border-t border-white/10 px-3 py-1.5 text-[11px] text-slate-300">
          {h.organization_name} · {fmtDate(h.transfer_date)}
          {/* Комментарий заказа: у завоза в комнату ФБС там названо ЮРЛИЦО,
              ради которого везут. Больше нигде в документе его нет. */}
          {h.comment && (
            <div className="truncate text-slate-400">{h.comment}</div>
          )}
        </div>
      )}
      {menuOpen && menu && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-2 top-[44px] z-40 w-56 overflow-hidden rounded-lg bg-white text-slate-800 shadow-xl">
            {menu.showCamera && (
              <button
                type="button"
                className={item}
                onClick={() => {
                  setMenuOpen(false);
                  menu.onCamera();
                }}
              >
                Камера-сканер
              </button>
            )}
            <button
              type="button"
              className={item}
              onClick={() => {
                setMenuOpen(false);
                menu.onManual();
              }}
            >
              Ввести код вручную
            </button>
            <button
              type="button"
              className={item}
              onClick={() => {
                setMenuOpen(false);
                menu.onReloadIndex();
              }}
            >
              Повторить загрузку индекса ШК
            </button>
            <button
              type="button"
              className={cn(item, "text-red-600")}
              onClick={() => {
                setMenuOpen(false);
                menu.onClear();
              }}
            >
              Очистить перемещение полностью
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Progress({
  detail,
  gate,
  onTap,
}: {
  detail: TransferSessionDetail;
  gate: GateInfo;
  onTap: () => void;
}): React.ReactElement {
  const sc = detail.qty_scanned_total;
  const nd = detail.qty_needed_total;
  const unit = commonUnit(detail.needs);
  const pct = nd > 0 ? Math.min(100, (sc / nd) * 100) : 0;
  const exact = gate.state === "exact";
  const bar = exact ? "bg-emerald-500" : "bg-amber-500";
  return (
    <button
      type="button"
      onClick={onTap}
      className="w-full border-b bg-white px-3.5 py-2 text-left"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-slate-500">
          Собрано
        </span>
        <span className="flex items-center gap-1">
          <span
            className={cn(
              "font-mono text-[17px] font-bold tabular-nums",
              gate.over > REMAINING_EPS && "text-amber-700",
            )}
          >
            {fmtQty(sc)}
          </span>
          <span className="text-[13px] text-slate-500">
            / {withUnit(nd, unit)}
          </span>
          <ChevronRight className="size-4 text-slate-300" />
        </span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded bg-slate-200">
        <div
          className={cn("h-full rounded transition-[width]", bar)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] font-bold">
        {gate.under > REMAINING_EPS && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
            Осталось {withUnit(gate.under, unit)} · {gate.underLines}{" "}
            {plural(gate.underLines, ["позиция", "позиции", "позиций"])}
          </span>
        )}
        {gate.over > REMAINING_EPS && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
            Сверх заказа +{withUnit(gate.over, unit)}
          </span>
        )}
        {gate.extras > 0 && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
            Нет в заказе: {gate.extras}
          </span>
        )}
      </div>
    </button>
  );
}

// ─────────────────────────── Hero ─────────────────────────────────────────
function Hero({
  hero,
  remaining,
  unit,
  onUndo,
  onSetQty,
  onDismiss,
}: {
  hero: Hero;
  remaining: number;
  /** Общая базовая единица заказа или пусто. */
  unit: string;
  onUndo: (itemId: string) => void;
  onSetQty: (itemId: string, units: number, coef: number) => void;
  onDismiss: () => void;
}): React.ReactElement {
  if (hero.kind === "idle") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <ScanTarget />
        <div className="text-[17px] font-bold text-slate-700">
          Сканируйте товар
        </div>
        <div className="text-[12px] text-slate-400">
          осталось {withUnit(Math.max(0, remaining), unit)} по перемещению
        </div>
      </div>
    );
  }
  if (hero.kind === "busy") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 text-center">
        <Loader2 className="size-7 animate-spin text-sky-600" />
        <div className="text-[14px] font-bold text-sky-700">Ищем в 1С…</div>
        <div className="font-mono text-[11px] text-slate-500">
          {hero.raw.slice(0, 40)}
        </div>
      </div>
    );
  }
  if (hero.kind === "error") {
    return (
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3">
        <div className="text-[18px] font-bold text-red-700">{hero.title}</div>
        <div className="mt-1 whitespace-pre-line text-[12px] text-red-900">{hero.detail}</div>
        {hero.raw && (
          <div className="mt-2 break-all rounded bg-white p-2 font-mono text-[11px] text-slate-500">
            {hero.raw}
          </div>
        )}
        <div className="mt-2 flex gap-2">
          {hero.retry && (
            <Button
              className="h-12 flex-1 bg-red-700 hover:bg-red-800"
              onClick={hero.retry}
            >
              <RotateCcw className="size-4" /> Повторить
            </Button>
          )}
          <Button variant="outline" className="h-12 flex-1" onClick={onDismiss}>
            Продолжить скан
          </Button>
        </div>
      </div>
    );
  }
  if (hero.kind === "box") return <BoxAcceptedCard box={hero.box} />;
  // ok
  const it = hero.item;
  // Единицы (коробов/штук), а не базовые штуки: правим то, что человек считает
  // руками, коэффициент домножает сервер.
  const units = it.coef > 1 ? it.added / it.coef : it.added;
  const scannedNow = it.need ? it.need.qty_scanned + it.added : 0;
  const st = it.need
    ? needStatus({ qty_needed: it.need.qty_needed, qty_scanned: scannedNow })
    : "idle";
  const remain = it.need ? Math.max(0, it.need.qty_needed - scannedNow) : 0;
  const nCoef = it.need && it.need.unit_coef > 1 ? it.need.unit_coef : 1;
  const nUnit = it.need ? unitLabel(it.need) : it.unit;
  const showN = (base: number): string =>
    fmtQty(nCoef > 1 ? base / nCoef : base);
  // Сверх заказа и не из заказа — принято, но подсвечено: «Завершить»
  // перепишет заказ под факт, и кладовщик должен это видеть сразу.
  const flagged = !it.need || st === "over";
  return (
    <div
      key={it.itemId}
      data-testid="hero-ok"
      className={cn(
        "oc-flash rounded-xl border-2 p-3",
        flagged
          ? "border-amber-400 bg-amber-50"
          : "border-emerald-300 bg-emerald-50/60",
      )}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-[21px] font-bold">{it.article}</span>
        {it.coef > 1 && (
          <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
            короб ×{fmtQty(it.coef)}
          </span>
        )}
        <Check
          className={cn(
            "ml-auto size-5",
            flagged ? "text-amber-600" : "text-emerald-600",
          )}
        />
      </div>
      {!it.need && (
        <div className="mt-1 rounded-md border border-amber-300 bg-amber-100 px-2 py-1 text-[12px] font-bold text-amber-800">
          Нет в заказе — строка будет дописана в заказ
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <Tile label="ХАРАКТ." value={it.char || "без характ."} />
        <Tile
          label="ПАРТИЯ"
          value={it.partia || "без партии"}
          mono={!!it.partia}
        />
        <Tile
          label="В КОРОБЕ"
          value={it.coef > 1 ? `× ${withUnit(it.coef, nUnit)}` : nUnit || "единица"}
        />
        <Tile label="ДОБАВЛЕНО" value={`+${withUnit(it.added, nUnit)}`} accent />
      </div>

      {/* Сколько ОДИНАКОВЫХ упаковок взяли: со склада берут пачками, а скан
          даёт одну. Без этого «привезли 10 коробов» — десять сканов. Гейт
          свободного остатка проверяет добавку на бэкенде. */}
      <div className="mt-2 flex items-center justify-between rounded-lg border bg-white p-2">
        <span className="text-[12px] font-semibold text-slate-500">
          {it.coef > 1 ? "Коробов" : "Штук"} в этой позиции:
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="size-9 rounded-lg border text-[20px]"
            onClick={() => onSetQty(it.itemId, Math.max(1, units - 1), it.coef)}
            aria-label="Меньше"
          >
            −
          </button>
          <QtyInput
            value={units}
            onChange={(v) => v >= 1 && onSetQty(it.itemId, v, it.coef)}
          />
          <button
            type="button"
            className="size-9 rounded-lg border text-[20px]"
            onClick={() => onSetQty(it.itemId, units + 1, it.coef)}
            aria-label="Больше"
          >
            +
          </button>
        </div>
      </div>
      <div className="mt-2 line-clamp-2 text-[11px] text-slate-600">
        {it.name}
      </div>
      {it.need && (
        <div
          className={cn(
            "mt-2 rounded-md border p-2 text-[12px]",
            ROW_CLS[st],
            TEXT_CLS[st],
          )}
        >
          <div className="flex items-center justify-between">
            <span>
              нужно {showN(it.need.qty_needed)} · собрано {showN(scannedNow)}{" "}
              {nUnit}
            </span>
            <span className="text-[13px] font-bold">
              {st === "done"
                ? "✓ закрыто"
                : st === "over"
                  ? `Сверх заказа +${showN(scannedNow - it.need.qty_needed)} ${nUnit}`.trimEnd()
                  : `ОСТАЛОСЬ ${showN(remain)} ${nUnit}`.trimEnd()}
            </span>
          </div>
        </div>
      )}
      <Button
        variant="outline"
        className="mt-2 h-11 w-full"
        onClick={() => onUndo(it.itemId)}
      >
        <RotateCcw className="size-4" /> Отменить последний скан
      </Button>
    </div>
  );
}

function ScanTarget(): React.ReactElement {
  return (
    <div className="flex size-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-300">
      <svg
        viewBox="0 0 24 24"
        className="size-8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          d="M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3M3 12h18"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function Tile({
  label,
  value,
  mono,
  accent,
}: {
  label: string;
  value: string;
  mono?: boolean;
  accent?: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "rounded-lg border px-2 py-1.5",
        accent
          ? "border-emerald-300 bg-emerald-50"
          : "border-slate-200 bg-white",
      )}
    >
      <div className="text-[9px] font-bold text-slate-400">{label}</div>
      <div
        className={cn(
          "text-[15px] font-bold",
          mono && "font-mono",
          accent && "text-emerald-700",
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ─────────────────────────── Gate ─────────────────────────────────────────
function Gate({
  gate,
  finalizing,
  onTap,
}: {
  gate: GateInfo;
  finalizing: boolean;
  onTap: () => void;
}): React.ReactElement {
  let cls = "bg-slate-100 border-slate-200 text-slate-500";
  let label = "Отсканируйте товары";
  let sub = "";
  if (finalizing) {
    cls = "bg-emerald-600 text-white";
    label = "Создаём документ в 1С…";
  } else if (gate.state === "exact") {
    cls = "bg-emerald-600 text-white border-emerald-600";
    label = "✓ Завершить — создать перемещение";
  } else if (gate.state === "diff") {
    // Расхождение не блокирует: «Завершить» перепишет заказ под факт, а
    // диалог покажет, как именно.
    cls = "bg-amber-500 text-white border-amber-500";
    label = "Завершить — заказ изменится ›";
    sub = gateSummary(gate);
  }
  return (
    <div className="border-t bg-white p-2">
      <button
        type="button"
        disabled={gate.state === "empty" || finalizing}
        onClick={onTap}
        className={cn(
          "flex h-14 w-full flex-col items-center justify-center rounded-[10px] border text-[14px] font-bold leading-tight",
          cls,
        )}
      >
        <span className="flex items-center gap-2">
          {finalizing && <Loader2 className="size-4 animate-spin" />}
          {label}
        </span>
        {sub && !finalizing && (
          <span className="text-[11px] font-semibold opacity-90">{sub}</span>
        )}
      </button>
    </div>
  );
}

/** Гейт перемещения через приёмку: «Отправить» вместо «Завершить». Документ
 *  1С создаёт «Принять» получателя, поэтому расхождение с заказом тут только
 *  подсвечивается — заказ подгонится под принятое. */
function SendGate({
  gate,
  sending,
  onTap,
}: {
  gate: GateInfo;
  sending: boolean;
  onTap: () => void;
}): React.ReactElement {
  const empty = gate.state === "empty";
  return (
    <div className="border-t bg-white p-2">
      <button
        type="button"
        disabled={empty || sending}
        onClick={onTap}
        className={cn(
          "flex h-14 w-full flex-col items-center justify-center rounded-[10px] border text-[14px] font-bold leading-tight",
          empty
            ? "border-slate-200 bg-slate-100 text-slate-500"
            : "border-sky-700 bg-sky-700 text-white",
        )}
      >
        <span className="flex items-center gap-2">
          {sending ? <Loader2 className="size-4 animate-spin" /> : !empty && <Send className="size-4" />}
          {empty ? "Отсканируйте товары" : sending ? "Отправляем…" : "Отправить на приёмку"}
        </span>
        {!empty && !sending && (
          <span className="text-[11px] font-semibold opacity-90">
            {gate.state === "diff" ? gateSummary(gate) : "в 1С — после приёмки"}
          </span>
        )}
      </button>
    </div>
  );
}

// ─────────────────────────── SH1 ──────────────────────────────────────────
function ClarifySheet({
  sessionId,
  draft,
  setDraft,
  needs,
  busy,
  onCancel,
  onConfirm,
}: {
  sessionId: string;
  draft: Draft;
  setDraft: (d: Draft) => void;
  needs: NeedLine[];
  /** Запрос добавления в полёте — «Добавить» неактивна. */
  busy: boolean;
  onCancel: () => void;
  onConfirm: (loose: boolean, qty?: number) => void;
}): React.ReactElement {
  const r = draft.resolved;
  const [partiaInput, setPartiaInput] = useState(
    draft.partiaName || draft.scan.batch || "",
  );
  const partiaM = useMutation({
    mutationFn: (name: string) =>
      lookupTransferPartia(sessionId, r.nomenclature_ref, name),
    onSuccess: (p) =>
      setDraft({
        ...draft,
        partiaRef: p.ref,
        partiaName: p.name,
        partiaSource: "manual",
        withoutPartia: false,
      }),
    onError: (e) => {
      const st = (e as { response?: { status?: number } })?.response?.status;
      toast.error(
        st === 404
          ? "Партия не найдена в 1С"
          : extractErrorMessage(e, "Ошибка проверки партии"),
      );
    },
  });
  const needChar =
    r.needs_characteristic && !draft.charRef && !draft.withoutChar;
  // Партия не требуется — скан без неё штатный (прямой путь скана её и не
  // спрашивает). Иначе шторка «нет в заказе» для такого кода заперла бы
  // «Добавить» за вопросом, которого на ней нет (PartiaAsk не показан).
  const partiaReady = !r.needs_partia || draft.withoutPartia || !!draft.partiaRef;
  const coef = r.unit_coefficient;
  const loose = coef > 1 && draft.qty % coef !== 0;

  const charKey = draft.withoutChar ? "" : draft.charRef;
  const partiaKey = draft.withoutPartia ? "" : draft.partiaRef;
  // findNeed строит индекс потребностей — мемоизируем, чтобы не пересобирать на
  // каждый ре-рендер шторки (ввод партии / степпер количества).
  const need = useMemo(
    () => findNeed(needs, r.nomenclature_ref, charKey, partiaKey, coef),
    [needs, r.nomenclature_ref, charKey, partiaKey, coef],
  );
  const remaining = need ? Math.max(0, need.qty_needed - need.qty_scanned) : 0;
  const afterAdd = (need?.qty_scanned ?? 0) + draft.qty;
  const overE = need ? Math.max(0, afterAdd - need.qty_needed) : 0;
  // (Ном, хар) нет в заказе — подтверждение «дописать в заказ». Пока
  // характеристика не выбрана, судить рано: выбор может попасть в строку.
  const notInOrder = !needChar && need == null;
  const nomInOrder = needs.some((n) => n.nomenclature_ref === r.nomenclature_ref);
  const isBoxNeed = !!need && need.unit_coef > 1;
  const nCoef = isBoxNeed ? need!.unit_coef : 1;
  const nUnit = need ? unitLabel(need) : unitByNom(needs, r.nomenclature_ref);
  // Степпер — в БАЗОВЫХ единицах (draft.qty), подпись — их имя, не «кор.».
  const baseUnit = need ? (need.base_unit_name ?? "") : unitByNom(needs, r.nomenclature_ref);
  const showN = (base: number): string =>
    fmtQty(isBoxNeed ? base / nCoef : base);

  const canAdd = !needChar && partiaReady && draft.qty > 0;
  const reason = needChar
    ? "Выберите характеристику"
    : !partiaReady
      ? "Укажите партию или «без партии»"
      : draft.qty <= 0
        ? "Укажите количество"
        : "";

  return (
    <BottomSheet
      onClose={onCancel}
      panelClassName="max-h-[85%] overflow-y-auto"
    >
      <div
        className={cn(
          "flex items-start gap-2 p-3",
          notInOrder ? "bg-amber-50" : "bg-white",
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[18px] font-bold">
              {r.nomenclature_article}
            </span>
            {coef > 1 && (
              <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
                короб ×{fmtQty(coef)}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[16px] font-bold">
            {needChar
              ? "Выберите характеристику"
              : notInOrder
                ? "Нет в заказе — добавить?"
                : draft.mode === "partia"
                  ? "Укажите партию"
                  : "Уточните скан"}
          </div>
          <div className="text-[11px] text-slate-500 line-clamp-1">
            {r.nomenclature_name}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="flex size-8 items-center justify-center"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="space-y-3 p-3">
        {need ? (
          <div
            className={cn(
              "rounded-lg border p-2 text-[12px]",
              overE > 0
                ? "border-amber-300 bg-amber-50 text-amber-800"
                : afterAdd >= need.qty_needed - REMAINING_EPS
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-sky-200 bg-sky-50 text-sky-800",
            )}
          >
            <div>
              По перемещению: нужно <b>{showN(need.qty_needed)}</b> · собрано{" "}
              <b>{showN(need.qty_scanned)}</b> · осталось{" "}
              <b>{showN(remaining)}</b> {nUnit}
            </div>
            <div className="mt-0.5 font-semibold">
              {overE > 0
                ? `После добавления: ${showN(afterAdd)} — сверх заказа +${showN(overE)} ${nUnit}`
                : Math.abs(need.qty_needed - afterAdd) <= REMAINING_EPS
                  ? `После добавления позиция закроется точно (${showN(afterAdd)} ${nUnit})`
                  : `После добавления останется ${showN(need.qty_needed - afterAdd)} ${nUnit}`}
            </div>
          </div>
        ) : notInOrder ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-800">
            <div className="font-semibold">Строка будет дописана в заказ.</div>
            {/* Товар в заказе есть, а характеристика другая — назвать, какую
                ждёт заказ: чаще это мисскан, чем осознанная замена. */}
            {nomInOrder && (
              <div className="mt-0.5">
                {
                  notInOrderHero(
                    needs,
                    r.nomenclature_ref,
                    { ref: charKey, name: draft.withoutChar ? "" : draft.charName },
                    draft.withoutPartia ? "" : draft.partiaName,
                    "transfer",
                  ).detail
                }
              </div>
            )}
          </div>
        ) : null}

        {/* Характеристика — общий выбор (`lib/scan-ui`): он разворачивает
            серверный вариант «без характеристики» в пустую ссылку.
            ⚠️ Блок виден и ПОСЛЕ ответа, с подсветкой выбранного. Пряча его,
            я сделал ответ необратимым: «Без характеристики» — первая кнопка,
            промах по ней закрывал блок насовсем, и позиция уходила в документ
            1С с пустой характеристикой. Отменить внутри шторки было нечем. */}
        <CharacteristicChoice
          options={r.available_characteristics}
          answer={{ charRef: draft.charRef, charName: draft.charName, withoutChar: draft.withoutChar }}
          onPick={(c) => setDraft({ ...draft, ...charAnswer(c) })}
          required={needChar}
        />

        {(draft.mode === "partia" || r.needs_partia) && (
          <PartiaAsk
            value={partiaInput}
            onChange={setPartiaInput}
            hasPicked={!!draft.partiaRef}
            picked={draft.partiaName}
            onReset={() =>
              setDraft({ ...draft, partiaRef: "", partiaName: "", partiaSource: "" })
            }
            onLookup={(name) => partiaM.mutate(name)}
            busy={partiaM.isPending}
            withoutPartia={draft.withoutPartia}
            onWithoutPartia={(v) => setDraft({ ...draft, withoutPartia: v })}
          />
        )}

        <div className="rounded-lg border p-2">
          <div className="mb-1.5 text-[12px] font-semibold text-slate-500">
            Количество{baseUnit ? `, ${baseUnit}` : ""}:
          </div>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              className="size-12 rounded-lg border text-[22px]"
              onClick={() =>
                setDraft({ ...draft, qty: Math.max(0, draft.qty - 1) })
              }
            >
              −
            </button>
            <QtyInput
              value={draft.qty}
              onChange={(v) => setDraft({ ...draft, qty: v })}
            />
            <button
              type="button"
              className="size-12 rounded-lg border text-[22px]"
              onClick={() => setDraft({ ...draft, qty: draft.qty + 1 })}
            >
              +
            </button>
          </div>
          <div className="mt-1 text-center text-[11px] text-slate-500">
            {isBoxNeed && loose
              ? `частичный короб недопустим — нужны целые короба ×${fmtQty(nCoef)}`
              : coef > 1
                ? loose
                  ? "частичный короб — россыпь (коробов 0)"
                  : `= ${fmtQty(draft.qty / coef)} кор.`
                : "штуки"}
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-t p-3">
        <Button
          variant="outline"
          className="h-[52px] flex-[0.4]"
          onClick={onCancel}
        >
          Отмена
        </Button>
        <Button
          className={cn(
            "h-[52px] flex-1",
            notInOrder
              ? "bg-amber-600 hover:bg-amber-700"
              : "bg-emerald-600 hover:bg-emerald-700",
          )}
          disabled={!canAdd || busy}
          onClick={() => onConfirm(loose, draft.qty)}
        >
          {busy ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}{" "}
          {canAdd ? `Добавить ${withUnit(draft.qty, nUnit)}` : reason}
        </Button>
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── SH2 ──────────────────────────────────────────
/** Статус строки заказа против факта — что «Завершить» сделает со строкой. */
function lineStatusText(
  st: ReturnType<typeof needStatus>,
  diff: string,
): string {
  if (st === "done") return "✓ собрано точно";
  if (st === "over") return `СВЕРХ +${diff}`;
  if (st === "partial") return `НЕДОБОР −${diff}`;
  // Уберётся ли строка из заказа, знает только бэкенд (с учётом других
  // перемещений заказа) — точный список в диалоге «Завершить».
  return "НЕ СОБРАНО";
}

function PositionsSheet({
  detail,
  sourceStock,
  filter,
  onClose,
  onDeleteItem,
}: {
  detail: TransferSessionDetail;
  sourceStock: SourceStockState;
  filter: PosFilter;
  onClose: () => void;
  onDeleteItem?: (id: string) => void;
}): React.ReactElement {
  const [f, setF] = useState<PosFilter>(filter);
  const [expanded, setExpanded] = useState<string | null>(null);
  const idx = useMemo(() => buildNeedIndex(detail.needs), [detail.needs]);
  const stockByKey = useMemo(
    () =>
      new Map(
        (sourceStock.data?.rows ?? []).map((r) => [
          `${r.nomenclature_ref}|${r.characteristic_ref}`,
          r,
        ]),
      ),
    [sourceStock.data],
  );
  const needKeys = useMemo(
    () => new Set(detail.needs.map((n) => needGranKey(n))),
    [detail.needs],
  );
  const extraItemsByKey = useMemo(() => {
    const m: Record<string, { id: string; qty: number }[]> = {};
    for (const it of detail.items) {
      const key = granKey(
        it.nomenclature_ref ?? "",
        it.characteristic_ref ?? "",
        it.partia_ref ?? "",
        it.unit_coefficient,
        idx,
      );
      if (!needKeys.has(key))
        (m[key] ??= []).push({ id: it.id, qty: it.qty_base });
    }
    return m;
  }, [detail.items, needKeys, idx]);
  const needs = [...detail.needs].sort(
    (a, b) =>
      NEED_STATUS_ORDER[needStatus(a)] - NEED_STATUS_ORDER[needStatus(b)] ||
      a.nomenclature_name.localeCompare(b.nomenclature_name),
  );
  const shown = needs.filter((n) => {
    const st = needStatus(n);
    if (f === "remain") return st === "partial" || st === "idle";
    if (f === "diff") return st !== "done";
    if (f === "done") return st === "done";
    return true;
  });
  const counts = {
    remain: needs.filter((n) => ["partial", "idle"].includes(needStatus(n)))
      .length,
    diff:
      needs.filter((n) => needStatus(n) !== "done").length +
      detail.extras.length,
    done: needs.filter((n) => needStatus(n) === "done").length,
  };
  return (
    <BottomSheet onClose={onClose} panelClassName="flex max-h-[90%] flex-col">
      <div className="flex items-center justify-between border-b p-3">
        <div className="text-[15px] font-bold">Позиции перемещения</div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-slate-500">
            {fmtQty(detail.qty_scanned_total)} /{" "}
            {withUnit(detail.qty_needed_total, commonUnit(detail.needs))}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="flex size-8 items-center justify-center"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
      {sourceStock.error != null && (
        <div className="border-b bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-700">
          Остаток склада-источника не получен:{" "}
          {extractErrorMessage(sourceStock.error)}
        </div>
      )}
      <div className="flex gap-1.5 border-b p-2">
        {(
          [
            ["remain", `Осталось (${counts.remain})`],
            ["diff", `Расхождения (${counts.diff})`],
            ["done", `Собрано (${counts.done})`],
            ["all", "Все"],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setF(k)}
            className={cn(
              "h-9 flex-1 rounded-md text-[12px] font-semibold",
              f === k
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600",
            )}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
        {shown.map((n) => {
          const st = needStatus(n);
          const box = n.unit_coef > 1;
          const sN = (base: number): string =>
            fmtQty(box ? base / n.unit_coef : base);
          const u = unitLabel(n);
          return (
            <div
              key={`${n.nomenclature_ref}|${n.characteristic_ref}|${n.partia_ref}|${n.unit_coef}`}
              data-testid="position-row"
              className={cn("rounded-lg border p-2", ROW_CLS[st])}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="font-mono text-[14px] font-bold">
                      {n.nomenclature_article}
                    </span>
                    {n.characteristic_name && (
                      <span className="rounded border bg-white px-1 text-[10px]">
                        {n.characteristic_name}
                      </span>
                    )}
                    {n.partia_name && (
                      <span className="rounded border bg-white px-1 font-mono text-[10px]">
                        парт. {n.partia_name}
                      </span>
                    )}
                    {box && (
                      <span className="rounded border border-sky-200 bg-sky-100 px-1 text-[10px] font-bold text-sky-800">
                        короб ×{fmtQty(n.unit_coef)}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-600">
                    {n.nomenclature_name}
                  </div>
                  <div
                    className={cn("text-[11px] font-semibold", TEXT_CLS[st])}
                  >
                    {lineStatusText(
                      st,
                      u
                        ? `${sN(Math.abs(n.qty_scanned - n.qty_needed))} ${u}`
                        : sN(Math.abs(n.qty_scanned - n.qty_needed)),
                    )}
                  </div>
                  <SourceStockLine
                    n={n}
                    stock={sourceStock}
                    rowsByKey={stockByKey}
                    sN={sN}
                    u={u}
                  />
                </div>
                <div
                  className={cn(
                    "shrink-0 font-mono text-[17px] font-bold tabular-nums",
                    TEXT_CLS[st],
                  )}
                >
                  {sN(n.qty_scanned)}
                  <span className="text-[10px] text-slate-400">
                    /{sN(n.qty_needed)} {u}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {f !== "done" && f !== "remain" && detail.extras.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-2">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[13px] font-bold text-amber-800">
                НЕТ В ЗАКАЗЕ — допишется ({detail.extras.length})
              </div>
              {onDeleteItem && (
                <button
                  type="button"
                  onClick={() =>
                    Object.values(extraItemsByKey)
                      .flat()
                      .forEach((it) => onDeleteItem(it.id))
                  }
                  className="rounded bg-red-700 px-2 py-1 text-[11px] font-bold text-white"
                >
                  Удалить все
                </button>
              )}
            </div>
            {detail.extras.map((e) => {
              const key = `${e.nomenclature_ref}|${norm(e.characteristic_ref)}|${norm(e.partia_ref)}|${ck(e.unit_coef)}`;
              const items = extraItemsByKey[key] ?? [];
              const open = expanded === key;
              const wrongUnit =
                requiredBoxCoef(
                  detail.needs,
                  e.nomenclature_ref,
                  e.characteristic_ref ?? "",
                ) > 1;
              return (
                <div
                  key={key}
                  data-testid="extra-row"
                  className="mt-1 rounded border border-amber-200 bg-white p-1.5"
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : key)}
                    className="flex w-full items-center justify-between text-left text-[11px]"
                  >
                    <span className="min-w-0 truncate text-amber-900">
                      {e.nomenclature_name} {e.characteristic_name}
                      {e.partia_name ? ` · парт. ${e.partia_name}` : ""}
                      {wrongUnit
                        ? " · не та единица (нужны короба)"
                        : e.unit_coef > 1
                          ? ` · короб ×${fmtQty(e.unit_coef)}`
                          : ""}
                    </span>
                    <span className="ml-2 shrink-0 font-bold text-amber-800">
                      {withUnit(e.qty_scanned, unitByNom(detail.needs, e.nomenclature_ref))}{" "}
                      {onDeleteItem ? (open ? "▲" : "▼") : ""}
                    </span>
                  </button>
                  {open && onDeleteItem && (
                    <div className="mt-1 space-y-1">
                      {items.map((it) => (
                        <div
                          key={it.id}
                          className="flex items-center justify-between rounded bg-amber-50 px-1.5 py-1 text-[11px]"
                        >
                          <span>
                            {withUnit(
                              it.qty,
                              unitByNom(detail.needs, e.nomenclature_ref),
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => onDeleteItem(it.id)}
                            className="rounded bg-red-700 px-2 py-0.5 text-[10px] font-bold text-white"
                          >
                            Удалить
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {shown.length === 0 && detail.extras.length === 0 && (
          <div className="py-6 text-center text-[13px] text-slate-400">
            Нет позиций в этой группе
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── Лента позиций ────────────────────────────────
function ItemsSheet({
  detail,
  onClose,
  onDeleteItem,
}: {
  detail: TransferSessionDetail;
  onClose: () => void;
  onDeleteItem: (id: string) => void;
}): React.ReactElement {
  const items = [...detail.items].reverse();
  return (
    <BottomSheet onClose={onClose} panelClassName="flex max-h-[90%] flex-col">
      <div className="flex items-center justify-between border-b p-3">
        <div className="text-[15px] font-bold">
          Отсканировано ({items.length})
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex size-8 items-center justify-center"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
        {items.length === 0 && (
          <div className="py-6 text-center text-[13px] text-slate-400">
            Пока ничего не отсканировано
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            className="flex items-start justify-between gap-2 rounded-lg border bg-white p-2"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1">
                <span className="font-mono text-[13px] font-bold">
                  {it.nomenclature_article}
                </span>
                {it.characteristic_name && (
                  <span className="rounded border bg-slate-50 px-1 text-[10px]">
                    {it.characteristic_name}
                  </span>
                )}
                {it.partia_name && (
                  <span className="rounded border bg-slate-50 px-1 font-mono text-[10px]">
                    парт. {it.partia_name}
                  </span>
                )}
              </div>
              <div className="mt-0.5 truncate text-[11px] text-slate-600">
                {it.nomenclature_name}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="font-mono text-[15px] font-bold tabular-nums">
                {fmtQty(it.qty_base)} шт
              </span>
              <button
                type="button"
                onClick={() => onDeleteItem(it.id)}
                className="flex size-8 items-center justify-center rounded-md border border-red-200 text-red-600"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── SH4 ──────────────────────────────────────────
function ManualCodeSheet({
  onSubmit,
  onClose,
}: {
  onSubmit: (code: string) => void;
  onClose: () => void;
}): React.ReactElement {
  const [v, setV] = useState("");
  return (
    <BottomSheet onClose={onClose} panelClassName="p-3">
      <div className="mb-2 text-[15px] font-bold">Ввести код вручную</div>
      <div className="flex gap-2">
        <input
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="ШК / GTIN / GS1"
          className="h-[50px] flex-1 rounded-lg border px-3 font-mono text-[14px]"
        />
        <Button
          className="h-[50px] bg-sky-700"
          disabled={!v.trim()}
          onClick={() => onSubmit(v.trim())}
        >
          ОК
        </Button>
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── S4 completed ─────────────────────────────────
function CompletedScreen({
  detail,
  onNewCheck,
  onReopen,
  onClear,
  onOpenPositions,
  onBack,
  onPost,
  postBusy,
  dialog,
  onCloseDialog,
  onConfirmReopen,
  onConfirmClear,
  reopenBusy,
  sheet,
  onCloseSheet,
  sourceStock,
}: {
  detail: TransferSessionDetail;
  onNewCheck: () => Promise<void>;
  onReopen: () => void;
  onClear: () => void;
  onOpenPositions: () => void;
  onBack: () => void;
  onPost: () => void;
  postBusy: boolean;
  dialog: null | "fin" | "send" | "reopen" | "clear";
  onCloseDialog: () => void;
  onConfirmReopen: () => void;
  onConfirmClear: () => void;
  reopenBusy: boolean;
  sheet: null | { kind: "positions"; filter: PosFilter };
  onCloseSheet: () => void;
  sourceStock: SourceStockState;
}): React.ReactElement {
  const h = detail.header;
  const { user } = useAuth();
  // Через приёмку: документ создан по ПРИНЯТОМУ получателем. «Вернуть в
  // работу» / «Очистить» отменили бы его приёмку — не-админу бэк отвечает
  // 409, кнопок у него нет; админу — с предупреждением в диалоге.
  const viaReceipt = h.requires_receipt;
  const canReopen = !viaReceipt || !!user?.is_admin;
  const receiptWarn = viaReceipt
    ? " Приёмка получателя будет сброшена: принятое им и решение по расхождению пропадут."
    : "";
  return (
    <>
      <AppBar detail={detail} chip="paused" onBack={onBack} />
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <div className="flex items-center gap-2 text-[16px] font-bold text-emerald-700">
            <CheckCircle2 className="size-5" /> {viaReceipt ? "Принято получателем" : "Проверка завершена"}
          </div>
          {viaReceipt && (
            <div data-testid="receipt-accepted" className="mt-1 text-[13px] text-emerald-900">
              Принял склад «{h.to_name || "—"}» · {fmtDateTime(h.received_at) || "—"}
              <div>
                {h.resolution === "by_receipt"
                  ? "По принятому (после разбора расхождения)"
                  : h.resolution === "match"
                    ? "Принятое совпало с отправленным"
                    : `Решение: ${h.resolution ?? "—"}`}
              </div>
            </div>
          )}
          {h.transfer_doc_number && (
            <div className="mt-1 text-[13px] text-emerald-900">
              Перемещение запасов: <b>{h.transfer_doc_number}</b>{" "}
              {h.transfer_doc_posted ? "(проведено)" : "(не проведено)"}
            </div>
          )}
        </div>
        {h.transfer_doc_number && !h.transfer_doc_posted && (
          <Button className="h-12 w-full" disabled={postBusy} onClick={onPost}>
            {postBusy ? "Проводим…" : "Провести перемещение"}
          </Button>
        )}
        <button
          type="button"
          onClick={onOpenPositions}
          className="flex w-full items-center justify-between rounded-lg border bg-white p-3 text-left"
        >
          <span className="text-[13px] font-semibold">
            Собрано {fmtQty(detail.qty_scanned_total)} /{" "}
            {withUnit(detail.qty_needed_total, commonUnit(detail.needs))}
          </span>
          <ChevronRight className="size-4 text-slate-300" />
        </button>
        <Button
          variant="outline"
          className="h-12 w-full"
          onClick={() => void onNewCheck()}
        >
          <Plus className="size-4" /> Новая проверка этого перемещения
        </Button>
        {canReopen && (
          <>
            <Button variant="outline" className="h-12 w-full" onClick={onReopen}>
              <RotateCcw className="size-4" /> Вернуть в работу
            </Button>
            <button
              type="button"
              onClick={onClear}
              className="w-full text-center text-[12px] text-red-600"
            >
              Ещё… → Очистить перемещение полностью
            </button>
          </>
        )}
      </div>

      {sheet?.kind === "positions" && (
        <PositionsSheet
          detail={detail}
          sourceStock={sourceStock}
          filter={sheet.filter}
          onClose={onCloseSheet}
        />
      )}
      {dialog === "reopen" && (
        <Dialog
          title="Вернуть в работу?"
          body={`Документ в 1С будет распроведён и помечен на удаление. Можно будет править сборку.${receiptWarn}`}
          confirmLabel="Вернуть"
          confirmBusy={reopenBusy}
          onConfirm={onConfirmReopen}
          onCancel={onCloseDialog}
        />
      )}
      {dialog === "clear" && (
        <Dialog
          title="Очистить перемещение полностью?"
          body={`Все сканы будут удалены, документ распроведён и помечен на удаление.${receiptWarn}`}
          confirmLabel="Очистить"
          confirmBusy={reopenBusy}
          danger
          onConfirm={onConfirmClear}
          onCancel={onCloseDialog}
        />
      )}
    </>
  );
}

// ─────────────────────── отправлено / разбор / отменено ───────────────────
/** Статус перемещения через приёмку — что с ним сейчас и кто ход делает. */
const FROZEN_VIEW = {
  sent: {
    cls: "border-sky-200 bg-sky-50 text-sky-800",
    icon: Truck,
    title: "В пути на приёмку",
    text: "Получатель сканирует привезённое и нажимает «Принять». Документ 1С создастся и проведётся после приёмки.",
  },
  discrepancy: {
    cls: "border-amber-300 bg-amber-50 text-amber-800",
    icon: TriangleAlert,
    title: "Расхождение — на разборе",
    text: "Принятое не совпало с отправленным. В 1С ничего не записано, пока расхождение не разберут («Расхождения перемещений»).",
  },
  cancelled: {
    cls: "border-slate-300 bg-slate-100 text-slate-700",
    icon: Ban,
    title: "Отменено",
    text: "В 1С ничего не записано. Заказ можно собрать заново новой проверкой.",
  },
} as const;

function FrozenScreen({
  detail,
  onBack,
  onNewCheck,
  sheet,
  onOpenPositions,
  onCloseSheet,
  sourceStock,
}: {
  detail: TransferSessionDetail;
  onBack: () => void;
  onNewCheck: () => Promise<void>;
  sheet: null | { kind: "positions"; filter: PosFilter };
  onOpenPositions: () => void;
  onCloseSheet: () => void;
  sourceStock: SourceStockState;
}): React.ReactElement {
  const h = detail.header;
  const view = FROZEN_VIEW[h.status as keyof typeof FROZEN_VIEW];
  const Icon = view.icon;
  return (
    <>
      <AppBar detail={detail} chip="paused" onBack={onBack} />
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        <div data-testid="frozen-status" className={cn("rounded-lg border p-3", view.cls)}>
          <div className="flex items-center gap-2 text-[16px] font-bold">
            <Icon className="size-5" /> {view.title}
          </div>
          <div className="mt-1 text-[13px]">{view.text}</div>
          <div className="mt-2 text-[12px] opacity-80">
            Куда: <b>{h.to_name || "—"}</b>
            {h.sent_at && (
              <>
                {" "}· отправлено {fmtDateTime(h.sent_at)}
                {h.sent_by_name ? ` (${h.sent_by_name})` : ""}
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenPositions}
          className="flex w-full items-center justify-between rounded-lg border bg-white p-3 text-left"
        >
          <span className="text-[13px] font-semibold">
            Отправлено позиций: {positionsCount(detail.items)} ·{" "}
            {withUnit(detail.qty_scanned_total, commonUnit(detail.needs))}
          </span>
          <ChevronRight className="size-4 text-slate-300" />
        </button>
        {h.status === "cancelled" && (
          <Button variant="outline" className="h-12 w-full" onClick={() => void onNewCheck()}>
            <Plus className="size-4" /> Новая проверка этого перемещения
          </Button>
        )}
      </div>
      {sheet?.kind === "positions" && (
        <PositionsSheet
          detail={detail}
          sourceStock={sourceStock}
          filter={sheet.filter}
          onClose={onCloseSheet}
        />
      )}
    </>
  );
}
